import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { registryKeys } from '../queryKeys';
import { registryRecordClient } from '../records/registryRecordClient';
import { asOfTimestamp } from './model';
/** "{tenant}:{user}": seats, moves and panels are filtered per viewer on the server. */ function useViewerKey() {
    const { user } = useAuth();
    return `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
}
const key = (viewer, ...rest)=>[
        ...registryKeys.all,
        'people-movement',
        viewer,
        ...rest
    ];
export function useOrgSeats(org, year) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: key(viewer, 'seats', org?.kind, org?.id, year),
        enabled: !!org,
        retry: false,
        queryFn: ()=>registryRecordClient.listOrgSeats({
                employerKind: org?.kind,
                employerId: org?.id,
                asOf: asOfTimestamp(year)
            })
    });
}
export function usePeopleMoves(includeAcquisitions, sinceYear) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: key(viewer, 'moves', includeAcquisitions, sinceYear ?? 'all'),
        retry: false,
        queryFn: ()=>registryRecordClient.listPeopleMoves({
                includeAcquisitions,
                fromDate: sinceYear === undefined ? undefined : Timestamp.fromDate(new Date(Date.UTC(sinceYear, 0, 1)))
            })
    });
}
export function usePersonCareer(contactId) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: key(viewer, 'career', contactId),
        enabled: !!contactId,
        retry: false,
        queryFn: ()=>registryRecordClient.getPersonCareerPanel({
                contactId
            })
    });
}
/** Every seat write refreshes the registry caches (chart, moves and career panel). */ export function useSeatWrites() {
    const qc = useQueryClient();
    const done = ()=>qc.invalidateQueries({
            queryKey: registryKeys.all
        });
    const client = registryRecordClient;
    return {
        save: useMutation({
            mutationFn: (req)=>client.saveOrgSeat(req),
            onSuccess: done
        }),
        retire: useMutation({
            mutationFn: (req)=>client.retireOrgSeat(req),
            onSuccess: done
        }),
        remove: useMutation({
            mutationFn: (req)=>client.deleteOrgSeat(req),
            onSuccess: done
        }),
        assign: useMutation({
            mutationFn: (req)=>client.assignEmploymentToSeat(req),
            onSuccess: done
        })
    };
}
